import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// § 3.14 Tier 2 — single page-level "Send for Signature" modal.
//
// Listens on the window for `docusign-send-modal:open` events fired by
// per-source trigger buttons (`docusign_send_modal_trigger_controller`),
// pre-fills the source + email subject/body + default signers, and
// POSTs to `/integrations/esignature/send_envelope` on submit.
export default class extends Controller {
  static targets = [
    "root", "form", "sourceType", "sourceId", "sourceLabel",
    "subject", "body", "signerList", "signerTemplate", "submit"
  ]
  static values = {
    endpoint: String,
    connectionId: String
  }

  connect() {
    this._closed = true
  }

  openFromEvent(event) {
    const detail = event.detail || {}
    this.sourceTypeTarget.value = detail.sourceType || ""
    this.sourceIdTarget.value = detail.sourceId || ""
    this.sourceLabelTarget.textContent = detail.sourceLabel ? `· ${detail.sourceLabel}` : ""
    this.subjectTarget.value = detail.defaultSubject || ""
    this.bodyTarget.value = detail.defaultBody || ""
    this._populateSigners(detail.defaultSigners || [])
    this.rootTarget.classList.remove("hidden")
    this._closed = false
    track("Send for Signature Modal Opened", {
      source_type: detail.sourceType,
      source_id: detail.sourceId
    })
  }

  close(event) {
    if (event) event.preventDefault()
    this.rootTarget.classList.add("hidden")
    this._closed = true
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  addSigner() {
    this._appendSignerRow({ email: "", name: "", routing_order: this._nextRoutingOrder() })
  }

  removeSigner(event) {
    const row = event.currentTarget.closest("[data-signer-row]")
    if (row) row.remove()
    this._renumberRoutingOrders()
  }

  async submit(event) {
    event.preventDefault()
    if (this._closed) return
    this.submitTarget.disabled = true
    const originalLabel = this.submitTarget.innerHTML
    this.submitTarget.innerHTML = "Sending…"

    try {
      const formData = new FormData(this.formTarget)
      const csrfMeta = document.querySelector('meta[name="csrf-token"]')
      const headers = { Accept: "application/json" }
      if (csrfMeta) headers["X-CSRF-Token"] = csrfMeta.content
      const res = await fetch(this.endpointValue, {
        method: "POST",
        body: formData,
        headers,
        credentials: "same-origin"
      })
      const payload = await res.json().catch(() => ({}))
      if (res.ok && payload.enqueued) {
        this._showToast("Envelope queued — DocuSign will email signers shortly.", "notice")
        track("Send for Signature Modal Submitted", {
          source_type: this.sourceTypeTarget.value,
          source_id: this.sourceIdTarget.value
        })
        this.close()
      } else {
        const reason = (payload && payload.error) || `HTTP ${res.status}`
        this._showToast(`Couldn't send envelope — ${reason}.`, "alert")
      }
    } catch (e) {
      this._showToast("Network error — please try again.", "alert")
    } finally {
      this.submitTarget.disabled = false
      this.submitTarget.innerHTML = originalLabel
    }
  }

  _populateSigners(rows) {
    this.signerListTarget.innerHTML = ""
    if (!rows.length) {
      this.addSigner()
      return
    }
    rows.forEach((row, idx) => this._appendSignerRow({
      email: row.email || "",
      name: row.name || "",
      routing_order: row.routing_order || (idx + 1).toString()
    }))
  }

  _appendSignerRow({ email, name, routing_order }) {
    const tpl = this.signerTemplateTarget
    const clone = tpl.content.firstElementChild.cloneNode(true)
    const inputs = clone.querySelectorAll("input")
    inputs[0].value = email || ""
    inputs[1].value = name || ""
    inputs[2].value = routing_order || this._nextRoutingOrder()
    this.signerListTarget.appendChild(clone)
  }

  _nextRoutingOrder() {
    const rows = this.signerListTarget.querySelectorAll("[data-signer-row]")
    return (rows.length + 1).toString()
  }

  _renumberRoutingOrders() {
    const rows = this.signerListTarget.querySelectorAll("[data-signer-row]")
    rows.forEach((row, idx) => {
      const orderInput = row.querySelectorAll("input")[2]
      if (orderInput && !orderInput.value.trim()) {
        orderInput.value = (idx + 1).toString()
      }
    })
  }

  _showToast(message, type) {
    const toast = document.createElement("div")
    toast.className = type === "notice" ? "flash-toast-notice" : "flash-toast-alert"
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.textContent = message
    document.body.prepend(toast)
  }
}
